import clsx from 'clsx'
import ValueBlock from '../../ValueBlock'
import useValueProps from '../../hooks/useValueProps'
import type { ValueProps } from '../../types'
import { omitSpacingProps } from '../../../../components/flex/utils'
import type { NumberFormatProps } from '../../../../components/NumberFormat'
import NumberFormatBase, {
  type NumberFormatInternalFormatter,
} from '../../../../components/number-format/NumberFormatBase'
import { formatCurrency } from '../../../../components/number-format/utils/formatCurrency'
import { formatPercent } from '../../../../components/number-format/utils/formatPercent'
import { formatNumber } from '../../../../components/number-format/utils/formatNumber'

import type { SpacingProps } from '../../../../shared/types'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type ValueNumberProps = Omit<ValueProps<number>, 'defaultValue'> &
  Omit<
    NumberFormatProps,
    keyof SpacingProps
    // spacing props is handled by ValueBlock
  > &
  Partial<{
    defaultValue?: number | string
    minimum?: number
    maximum?: number
    /** Formats the value as a percentage. */
    percent?: boolean
  }>

function NumberValue(props: ValueNumberProps) {
  const {
    value: valueProp,
    minimum = Number.MIN_SAFE_INTEGER,
    maximum = Number.MAX_SAFE_INTEGER,
    inline,
    showEmpty,
    className,
    path,
    itemPath,
    inheritLabel,
    ...rest
    // @ts-expect-error - strictFunctionTypes
  } = useValueProps(props)
  const { percent, ...numberFormatRest } = omitSpacingProps(
    rest
  ) as ValueNumberProps & {
    percent?: boolean
  }

  let value = valueProp
  if (value < minimum) {
    value = minimum
  }
  if (value > maximum) {
    value = maximum
  }

  const formatter: NumberFormatInternalFormatter = percent
    ? formatPercent
    : numberFormatRest.currency === true ||
        typeof numberFormatRest.currency === 'string'
      ? formatCurrency
      : formatNumber

  return (
    <ValueBlock
      className={clsx('dnb-forms-value-number', className)}
      inline={inline}
      showEmpty={showEmpty}
      {...rest}
    >
      {typeof value !== 'undefined' || showEmpty ? (
        <NumberFormatBase
          value={value}
          {...numberFormatRest}
          __format={formatter}
        />
      ) : null}
    </ValueBlock>
  )
}

withComponentMarkers(NumberValue, {
  _supportsSpacingProps: true,
})

export default NumberValue
